/**
 * The icon set, drawn inline.
 *
 * `DESIGN.md` §4 names lucide icons and — more importantly — names the property
 * that matters: **the silhouettes must differ**, so an octagon, a triangle and a
 * circle are still distinguishable in the greyscale screenshot
 * `docs/UI_PROCESS.md` §4 requires and Task 43 automates. Colour is the third
 * channel and never the first (spec 01 §19).
 *
 * These are hand-drawn to lucide's 24×24 grid and its 2 px stroke rather than
 * pulled from `lucide-react`. Ten glyphs against a 1 400-icon package is not a
 * trade this repository makes (G2's minimal dependency set), and drawing them here
 * means the silhouette contract above is visible in the source that has to keep it.
 *
 * Every glyph is `stroke="currentColor"` with no fill, so it takes the colour of the
 * text beside it and there is no colour literal anywhere in this file.
 */

import type { ReactNode } from 'react';

export type IconName =
  | 'octagon-alert'
  | 'triangle-alert'
  | 'circle-alert'
  | 'circle-check'
  | 'info'
  | 'clock'
  | 'lock'
  | 'ban'
  | 'inbox'
  | 'refresh'
  | 'play'
  | 'pause'
  | 'square'
  | 'hourglass'
  | 'arrow-right-to-line'
  | 'arrow-left-to-line'
  | 'fold-horizontal'
  | 'moon'
  | 'calendar-check'
  | 'sliders-horizontal'
  | 'chevron-down'
  | 'search'
  | 'calendar'
  | 'activity'
  | 'cpu'
  | 'bell'
  | 'bar-chart'
  | 'bot'
  | 'list'
  | 'user'
  | 'shield'
  | 'wallet'
  | 'map-pin'
  | 'users'
  | 'briefcase'
  | 'file';

const PATHS: Readonly<Record<IconName, readonly ReactNode[]>> = {
  // A regular octagon — the widest silhouette in the set, for the loudest severity.
  'octagon-alert': [
    <path key="o" d="M7.9 2.1h8.2L21.9 7.9v8.2L16.1 21.9H7.9L2.1 16.1V7.9z" />,
    <path key="s" d="M12 8v5" />,
    <path key="d" d="M12 16.5v.01" />,
  ],
  'triangle-alert': [
    <path key="t" d="M12 2.8 22 20H2z" />,
    <path key="s" d="M12 9v4.5" />,
    <path key="d" d="M12 17v.01" />,
  ],
  'circle-alert': [
    <circle key="c" cx="12" cy="12" r="9.2" />,
    <path key="s" d="M12 7.5v5" />,
    <path key="d" d="M12 16v.01" />,
  ],
  // Outlined circle plus a tick: a different interior mark, not a different colour.
  'circle-check': [
    <circle key="c" cx="12" cy="12" r="9.2" />,
    <path key="k" d="m8 12.3 2.7 2.7L16 9.6" />,
  ],
  // Thin outlined circle with a lower-case i — the quietest silhouette.
  info: [
    <circle key="c" cx="12" cy="12" r="9.2" />,
    <path key="s" d="M12 11v5.5" />,
    <path key="d" d="M12 7.7v.01" />,
  ],
  clock: [<circle key="c" cx="12" cy="12" r="9.2" />, <path key="h" d="M12 6.8V12l3.4 2" />],
  lock: [
    <rect key="b" x="3.5" y="10.5" width="17" height="10.5" rx="2" />,
    <path key="s" d="M7.6 10.5V7a4.4 4.4 0 0 1 8.8 0v3.5" />,
  ],
  ban: [<circle key="c" cx="12" cy="12" r="9.2" />, <path key="s" d="M5.5 5.5 18.5 18.5" />],
  inbox: [
    <path key="b" d="M3 13.5h4.5l1.6 2.8h5.8l1.6-2.8H21" />,
    <path key="o" d="M5.2 4.5h13.6l2.2 9v5a1.5 1.5 0 0 1-1.5 1.5H4.5A1.5 1.5 0 0 1 3 18.5v-5z" />,
  ],
  refresh: [
    <path key="a" d="M20.5 11.5a8.5 8.5 0 1 0-2 6.3" />,
    <path key="b" d="M20.5 5.5v6h-6" />,
  ],
  // ── Task 42 (WP-K2). Four glyphs, each rendered somewhere. ────────────────
  //
  // The three transport marks are the shapes every media control in the world
  // uses, and the reason to reuse them rather than invent is that a triangle, two
  // bars and a square are already distinguishable by silhouette to somebody who
  // has never read this product's documentation — which is §4's actual test.
  play: [<path key="p" d="M8 5.2 19 12 8 18.8z" />],
  pause: [<path key="l" d="M9 5.5v13" />, <path key="r" d="M15 5.5v13" />],
  square: [<rect key="s" x="6" y="6" width="12" height="12" rx="1.5" />],
  // Waiting on a job, not an error: an hourglass is neither a warning triangle nor
  // a clock face, so "chưa tính" cannot be mistaken for "late" or for "refused".
  hourglass: [
    <path key="t" d="M6.5 3h11" />,
    <path key="b" d="M6.5 21h11" />,
    <path key="u" d="M7.5 3v3.2L12 12l4.5-5.8V3" />,
    <path key="d" d="M7.5 21v-3.2L12 12l4.5 5.8V21" />,
  ],
  // ── Task 43 (WP-K3). Six glyphs, one per attendance status that had none. ──
  //
  // `web/shapes/attendance.md` §5 is the table these serve, and §5.2 is the test
  // that keeps them honest. The property being bought is **silhouette**: an arrow
  // against a bar, a mirrored arrow against a bar, two arrows converging on one, a
  // crescent, a calendar, and a row of sliders are six outlines a reader can tell
  // apart in the greyscale screenshot `docs/UI_PROCESS.md` §4 asks for.
  //
  // The three arrow marks are lucide's own names and its own metaphor — a clock-in
  // and a clock-out — rather than something invented here, because the direction of
  // an arrow against a boundary is already legible to somebody who has read no
  // documentation, and that is §4 rule 2's actual test.
  'arrow-right-to-line': [
    <path key="a" d="M3 12h13" />,
    <path key="h" d="m11 7 5 5-5 5" />,
    <path key="l" d="M20 4v16" />,
  ],
  'arrow-left-to-line': [
    <path key="a" d="M21 12H8" />,
    <path key="h" d="m13 7-5 5 5 5" />,
    <path key="l" d="M4 4v16" />,
  ],
  // Both arrows pointing inward at one line: late at one end and early at the other,
  // which is a third outline rather than either of the two above.
  'fold-horizontal': [
    <path key="l" d="M12 3v18" />,
    <path key="a" d="M3 12h6" />,
    <path key="ah" d="m6 9-3 3 3 3" />,
    <path key="b" d="M21 12h-6" />,
    <path key="bh" d="m18 9 3 3-3 3" />,
  ],
  // A crescent — nobody was expected in. Not a circle, so it is not confusable with
  // `circle-check`, `ban` or `info` at badge size.
  moon: [<path key="m" d="M20.5 14.4A8.8 8.8 0 1 1 9.6 3.5a7 7 0 0 0 10.9 10.9z" />],
  'calendar-check': [
    <rect key="b" x="3" y="5" width="18" height="16" rx="2" />,
    <path key="h" d="M3 10h18" />,
    <path key="l" d="M8 3v4" />,
    <path key="r" d="M16 3v4" />,
    <path key="k" d="m9 15.5 2.2 2.2 4-4" />,
  ],
  // Configuration, not a fault: the day has no shift because nobody configured one,
  // and `calc.ts` is explicit that a correction cannot fix that.
  'sliders-horizontal': [
    <path key="t" d="M3 7h11" />,
    <path key="tk" d="M17 7h4" />,
    <circle key="tc" cx="15.5" cy="7" r="1.8" />,
    <path key="b" d="M3 17h4" />,
    <path key="bk" d="M10 17h11" />,
    <circle key="bc" cx="8.5" cy="17" r="1.8" />,
  ],
  // ── The rail's sixteen, plus the one chevron every select draws. ───────────
  //
  // Same grid, same 2px stroke, same rule: the **silhouettes** differ, because a
  // rail of sixteen identical blobs is worse than a rail of none. They are hints
  // beside a word that already says the screen's name — never the label itself.
  'chevron-down': [<path key="c" d="m5 9 7 7 7-7" />],
  // A circle with a tail — the one round silhouette in the control set, so it is not
  // mistaken for the chevron a `.wp-select` draws at the same place in the field.
  search: [<circle key="o" cx="11" cy="11" r="6.5" />, <path key="t" d="m16 16 4.5 4.5" />],
  calendar: [
    <rect key="b" x="3" y="5" width="18" height="16" rx="2" />,
    <path key="h" d="M3 10h18" />,
    <path key="l" d="M8 3v4" />,
    <path key="r" d="M16 3v4" />,
  ],
  activity: [<path key="p" d="M3 12h4l3 8 4-16 3 8h4" />],
  cpu: [
    <rect key="o" x="5" y="5" width="14" height="14" rx="2" />,
    <rect key="i" x="9" y="9" width="6" height="6" rx="1" />,
    <path key="t" d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3" />,
  ],
  bell: [
    <path key="b" d="M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6" />,
    <path key="c" d="M10.3 20a2 2 0 0 0 3.4 0" />,
  ],
  'bar-chart': [
    <path key="a" d="M5 20V10" />,
    <path key="b" d="M12 20V4" />,
    <path key="c" d="M19 20v-6" />,
  ],
  bot: [
    <rect key="h" x="4" y="8" width="16" height="12" rx="2" />,
    <path key="a" d="M12 8V4" />,
    <circle key="d" cx="12" cy="3" r="1.4" />,
    <path key="e" d="M9 13v1.5M15 13v1.5" />,
  ],
  list: [
    <path key="a" d="M9 6h12M9 12h12M9 18h12" />,
    <path key="d" d="M4 6h.01M4 12h.01M4 18h.01" />,
  ],
  user: [<circle key="h" cx="12" cy="8" r="4" />, <path key="b" d="M4.5 20a7.5 7.5 0 0 1 15 0" />],
  shield: [<path key="s" d="M12 2.5 20 6v6c0 5-3.5 8.4-8 9.5-4.5-1.1-8-4.5-8-9.5V6z" />],
  wallet: [
    <path key="b" d="M3.5 7.5A2 2 0 0 1 5.5 5.5H18v3" />,
    <rect key="o" x="3.5" y="7.5" width="17" height="12" rx="2" />,
    <path key="d" d="M16.5 13.5h.01" />,
  ],
  'map-pin': [
    <path key="p" d="M12 21.5S5 15.5 5 10a7 7 0 0 1 14 0c0 5.5-7 11.5-7 11.5z" />,
    <circle key="c" cx="12" cy="10" r="2.6" />,
  ],
  users: [
    <circle key="h" cx="9" cy="8" r="3.6" />,
    <path key="b" d="M2.5 20a6.5 6.5 0 0 1 13 0" />,
    <path key="s" d="M16.5 4.6a3.6 3.6 0 0 1 0 6.9" />,
    <path key="t" d="M17.5 14.2A6.5 6.5 0 0 1 21.5 20" />,
  ],
  briefcase: [
    <rect key="b" x="3" y="7.5" width="18" height="12.5" rx="2" />,
    <path key="h" d="M8.5 7.5V5.5a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v2" />,
    <path key="l" d="M3 13h18" />,
  ],
  file: [
    <path
      key="o"
      d="M6 2.5h7l5 5V21a1.5 1.5 0 0 1-1.5 1.5h-10A1.5 1.5 0 0 1 5 21V4a1.5 1.5 0 0 1 1-1.5z"
    />,
    <path key="f" d="M13 2.5v5h5" />,
  ],
};

export interface IconProps {
  readonly name: IconName;
  readonly className?: string;
}

/**
 * `aria-hidden`, always. Every icon in this product sits next to its own text label
 * — that is the §4 rule — so announcing it would make a screen reader say the same
 * thing twice. An icon that ever ships without adjacent text is a defect in the
 * caller, not a missing `aria-label` here.
 */
export function Icon({ name, className }: IconProps): ReactNode {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      // 1.8, the weight the rail draws at (`shell.css`). One stroke weight rules the
      // product: a 2px state icon beside a 1.8px nav icon reads as a different set.
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      // Which glyph this is, for tests to lock a presentation table's icon choice
      // in against — the silhouette rule (this file's own header) is otherwise
      // unverifiable from the DOM, since every icon renders as an unlabelled,
      // `aria-hidden` `<svg>`. Purely diagnostic: nothing reads this at runtime.
      data-icon={name}
    >
      {PATHS[name]}
    </svg>
  );
}

/** DESIGN.md §4, the five rows of the severity table. */
export type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info';

/** The token/icon/label triple of §4, minus the label, which the catalogue owns. */
export const SEVERITY_ICON: Readonly<Record<Severity, IconName>> = {
  critical: 'octagon-alert',
  high: 'triangle-alert',
  medium: 'circle-alert',
  low: 'circle-check',
  info: 'info',
};
